import { useState } from "react";
import clsx from "clsx";
import { EditIcon, EllipsisIcon, TrashIcon } from "lucide-react";

import { CreateJobCommentForm } from "./CreateJobCommentForm";
import { UpdateJobCommentForm } from "./UpdateJobCommentForm";

import { Column, RichTextDisplay, Row, Text, VerificationBadge } from "@/components";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useDeleteJobComment } from "@/hooks/mutations/useDeleteJobComment";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { useGetJobComments } from "@/hooks/queries/useGetJobComments";
import { useGetUser } from "@/hooks/queries/useGetUser";
import { useRemoveJobComment } from "@/hooks/sets/useRemoveJobComment";
import { dates } from "@/lib/dates";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { tiptap } from "@/lib/tiptap";
import { JobComment as JobCommentType, Role } from "@/types";

const jsonToHtml = tiptap.jsonToHtmlFn();

const JobComment = ({ jobComment }: { jobComment: JobCommentType }) => {
  const { data: user, status } = useGetUser({
    userId: jobComment.userId,
  });
  const { mutate: deleteJobComment } = useDeleteJobComment();
  const removeJobComment = useRemoveJobComment();
  const [isEditing, setIsEditing] = useState(false);
  const currentUser = useGetCurrentUser();
  if (!currentUser) {
    return null;
  }

  if (status === "pending") {
    // TODO: Loading state.
    return null;
  } else if (status === "error") {
    // TODO: Error state.
    return null;
  }

  return (
    <>
      {isEditing ? (
        <UpdateJobCommentForm
          content={jobComment.content}
          jobCommentId={jobComment.id}
          onCancel={() => {
            setIsEditing(false);
          }}
          onSuccess={() => {
            setIsEditing(false);
          }}
        />
      ) : (
        <Column className="gap-2">
          <Row className="items-center justify-between">
            <Column className="gap-2">
              <Row className="items-center gap-2">
                <div className="relative">
                  <Row className="h-6 w-6 items-center justify-center rounded-full bg-slate-200">
                    <p className="text-[10px] font-medium text-slate-900">
                      {user.firstName[0]}
                      {user.lastName[0]}
                    </p>
                  </Row>
                  {user.role === Role.STAFFER && (
                    <VerificationBadge className="absolute right-[-6px] top-[15px]" />
                  )}
                </div>
                <Text variant="small">
                  {user.firstName} {user.lastName} •{" "}
                  {dates.timeAgoForPipeline(jobComment.createdAt)}
                </Text>
              </Row>
            </Column>
            {/* Only show the edit and delete dropdown if the current user is the author of the comment. */}
            {currentUser.id === user.id && (
              <DropdownMenu>
                <DropdownMenuTrigger>
                  <Button size="icon" variant="ghost">
                    <EllipsisIcon className="h-4 w-4 text-slate-700" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[110px] p-0">
                  <DropdownMenuItem
                    onClick={() => {
                      setIsEditing(true);
                    }}
                  >
                    <EditIcon className="h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-red-500"
                    onClick={() => {
                      mixpanelUtils.trackButtonClick(
                        "Delete comment",
                        MIXPANEL_SCREENS.JOB,
                      );
                      deleteJobComment(
                        {
                          jobCommentId: jobComment.id,
                        },
                        {
                          onSuccess: (deletedJobComment) => {
                            removeJobComment({
                              jobCommentId: deletedJobComment.id,
                            });
                          },
                        },
                      );
                    }}
                  >
                    <TrashIcon className="h-4 w-4" />
                    Remove
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </Row>
          <p className="whitespace-pre-line text-sm text-slate-700">
            <RichTextDisplay richText={jsonToHtml(jobComment.content)} />
          </p>
        </Column>
      )}
    </>
  );
};

export const JobComments = ({ className }: { className?: string }) => {
  const { data: comments, status } = useGetJobComments();

  if (status === "pending") {
    // TODO: Loading state.
    return null;
  } else if (status === "error") {
    // TODO: Error state.
    return null;
  }

  return (
    <Column className={clsx("gap-4", className)}>
      <CreateJobCommentForm />
      <Column className="gap-4">
        {comments.map((comment) => (
          <JobComment key={comment.id} jobComment={comment} />
        ))}
      </Column>
    </Column>
  );
};
